/**
 * StepPreScoreAts — wizard step 1.2 (Phase 1).
 *
 * Calculates a local ATS score for the CV built so far (no API call).
 * Optionally lets the user enter a job target to get a contextualised score.
 * Pure read-only — no data mutation.
 */

import { useMemo, useState } from 'react';
import { BarChart2, Target, CheckCircle2, AlertCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { analyzeCV, type ATSPreviewResult } from '../../../lib/ats-analyzer';
import type { CVData } from '../../../types';
import { useI18n } from '../../../contexts/I18nContext';

interface StepPreScoreAtsProps {
  data: CVData;
}

function FeedbackIcon({ type }: { type: 'success' | 'warning' | 'error' }) {
  if (type === 'success') return <CheckCircle2 className="size-3.5 text-emerald-400 shrink-0 mt-0.5" />;
  if (type === 'warning') return <AlertCircle className="size-3.5 text-amber-400 shrink-0 mt-0.5" />;
  return <XCircle className="size-3.5 text-red-400 shrink-0 mt-0.5" />;
}

export function StepPreScoreAts({ data }: StepPreScoreAtsProps) {
  const { t } = useI18n();
  const [jobTarget, setJobTarget] = useState('');
  const [showAll, setShowAll] = useState(false);

  const result: ATSPreviewResult = useMemo(
    () => analyzeCV(data, jobTarget || undefined),
    [data, jobTarget],
  );

  const { score } = result;
  const scoreColor = score >= 70 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  const ringColor = score >= 70 ? 'border-emerald-500/40' : score >= 50 ? 'border-amber-500/40' : 'border-red-500/40';

  const visibleFeedback = showAll ? result.feedback : result.feedback.slice(0, 4);

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center justify-center size-12 rounded-full bg-emerald-500/20 text-emerald-400 mb-2">
          <BarChart2 className="size-6" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">{t('wizard.atsScore')}</h1>
        <p className="text-base text-white/60 max-w-xl mx-auto">
          {t('wizard.atsScoreDesc')}
        </p>
      </div>

      {/* Score circle */}
      <div className="flex justify-center">
        <div className={`flex flex-col items-center justify-center size-36 rounded-full border-4 ${ringColor} bg-white/[0.02]`}>
          <span className={`text-5xl font-black ${scoreColor}`}>{score}</span>
          <span className="text-xs text-white/40 mt-1">/ 100</span>
        </div>
      </div>

      {/* Job target input */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-semibold text-white/70">
          <Target className="size-4" />
          {t('wizard.jobTarget')}
        </label>
        <input
          type="text"
          value={jobTarget}
          onChange={(e) => setJobTarget(e.target.value)}
          placeholder={t('wizard.targetPlaceholder')}
          className="w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
        />
        {jobTarget && result.missingKeywords.length > 0 && (
          <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <p className="text-xs font-semibold text-amber-300 mb-1.5">{t('wizard.missingKeywords')}</p>
            <div className="flex flex-wrap gap-1.5">
              {result.missingKeywords.slice(0, 12).map((kw) => (
                <span key={kw} className="px-2 py-0.5 rounded-full text-xs bg-amber-500/20 text-amber-200 border border-amber-500/20">{kw}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Feedback */}
      {result.feedback.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-white/60">{t('wizard.details')}</p>
          <div className="space-y-1.5">
            {visibleFeedback.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-xs text-white/70">
                <FeedbackIcon type={f.type} />
                <span>{f.message}</span>
              </div>
            ))}
          </div>
          {result.feedback.length > 4 && (
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="flex items-center gap-1 text-xs text-white/40 hover:text-white/60 transition-colors"
            >
              {showAll ? <><ChevronUp className="size-3" /> {t('wizard.showLess')}</> : <><ChevronDown className="size-3" /> {t('wizard.showMore').replace('{n}', String(result.feedback.length - 4))}</>}
            </button>
          )}
        </div>
      )}

      <p className="text-xs text-center text-white/30">
        {t('wizard.atsNextHint')}
      </p>
    </div>
  );
}
